import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import ROUTES from "@/routesData";
import { API } from "@/constants";
import FreightCalculator from "@/components/FreightCalculator";
import { useSiteSettings, waLink } from "@/siteSettings";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const fmtRp = (n) => `Rp ${n.toLocaleString("id-ID")}`;

export default function RoutePage() {
  const { slug } = useParams();
  const settings = useSiteSettings();
  const [tariffs, setTariffs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/tariffs`)
      .then((res) => setTariffs(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const curated = ROUTES.find((r) => r.slug === slug);
  const tariff = !curated && slug.startsWith("makassar-")
    ? tariffs.find((t) => slugify(t.name) === slug.slice("makassar-".length))
    : null;

  const route = curated || (tariff && {
    slug,
    title: `Ekspedisi Makassar ${tariff.name}`,
    destination: tariff.name,
    moda: tariff.moda || "Darat",
    desc: `Layanan ekspedisi dari Makassar ke ${tariff.name} (${tariff.region}) via jalur ${(tariff.moda || "darat").toLowerCase()}. Tarif handling resmi ${fmtRp(tariff.rate)} per ${tariff.unit === "m3" ? "m³" : "kg"}, minimal ${tariff.minKg} ${tariff.unit === "m3" ? "m³" : "kg"}, dengan estimasi tiba ${tariff.days}. Barang dijemput dari area Makassar atau diantar ke kantor kami di Jalan Gatot Subroto Baru No 17.`,
    points: [
      `Tarif resmi ${fmtRp(tariff.rate)}/${tariff.unit === "m3" ? "m³" : "kg"} — minimal ${tariff.minKg} ${tariff.unit === "m3" ? "m³" : "kg"}`,
      `Estimasi waktu pengiriman ${tariff.days}`,
      "Penjemputan barang area Makassar tersedia",
    ],
  });

  useEffect(() => {
    if (route) {
      document.title = `${route.title} | Nurrahma Logistics`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", `${route.title} — ${route.desc}`);
    }
    window.scrollTo(0, 0);
  }, [route]);

  if (!route) {
    if (!loaded) {
      return <div className="min-h-screen bg-[#F4F7FB] flex items-center justify-center text-sm text-slate-500" data-testid="route-loading">Memuat rute...</div>;
    }
    return (
      <div className="min-h-screen bg-[#F4F7FB] flex flex-col items-center justify-center px-4 text-center">
        <h1 className="text-2xl font-black text-blue-950 mb-3" data-testid="route-not-found">Rute tidak ditemukan</h1>
        <Link to="/" className="text-sm font-bold text-blue-900 hover:underline" data-testid="route-back-home">← Kembali ke Beranda</Link>
      </div>
    );
  }

  const others = ROUTES.filter((r) => r.slug !== slug).slice(0, 4);

  return (
    <div className="font-sans antialiased bg-[#F4F7FB] text-slate-800 min-h-screen">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3" data-testid="route-header-home">
            <img src="/logo-mark.png" alt="Nurrahma Logistics" className="h-10 w-auto" />
            <div>
              <span className="text-lg font-black tracking-tight text-blue-950 block leading-tight">NURRAHMA LOGISTICS</span>
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest">{settings.tagline}</span>
            </div>
          </Link>
          <Link to="/#kontak" className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-blue-900 text-amber-300 font-semibold text-sm hover:bg-blue-950 transition-colors shadow-sm" data-testid="route-cta-penawaran">
            Minta Penawaran
          </Link>
        </div>
      </header>

      <section className="bg-blue-950 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block px-3 py-1 rounded-full bg-amber-400/15 border border-amber-400/30 text-amber-300 text-xs font-semibold mb-5" data-testid="route-moda-badge">
            Moda: {route.moda}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight" data-testid="route-title">
            {route.title} — Tarif Kompetitif
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl" data-testid="route-desc">
            {route.desc}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link to="/#kontak" className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-amber-400 text-slate-950 font-bold text-base hover:bg-amber-300 transition-colors shadow-lg shadow-amber-400/20" data-testid="route-cta-form">
              Cek Ongkir ke {route.destination}
            </Link>
            <a href={waLink(settings)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white/10 text-white border border-white/25 font-bold text-base hover:bg-white/20 transition-colors" data-testid="route-cta-whatsapp">
              Chat WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-6">Kenapa Kirim ke {route.destination} Lewat Kami</h2>
          <ul className="space-y-3">
            {route.points.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-slate-700">
                <span className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 shrink-0"></span>
                {p}
              </li>
            ))}
          </ul>
          <div className="mt-10 p-5 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600 leading-relaxed">
            <span className="font-bold text-slate-900">Titik keberangkatan:</span> Jalan Gatot Subroto Baru No 17, Makassar, Sulawesi Selatan.
            Isi formulir permintaan tarif di beranda atau hubungi tim kami untuk jadwal keberangkatan terdekat.
          </div>
          <div className="mt-10">
            <FreightCalculator destination={route.destination} />
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#EEF2F7] border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-extrabold text-slate-900 tracking-tight mb-6">Rute Populer Lainnya</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {others.map((r) => (
              <Link key={r.slug} to={`/rute/${r.slug}`} className="p-4 rounded-xl bg-white border border-slate-200 hover:border-blue-300 transition-colors text-sm font-bold text-blue-900" data-testid={`route-link-${r.slug}`}>
                {r.title} →
              </Link>
            ))}
          </div>
          <p className="mt-8 text-center">
            <Link to="/" className="text-xs text-slate-500 hover:text-blue-900 transition-colors" data-testid="route-footer-home">← Kembali ke Beranda Nurrahma Logistics</Link>
          </p>
        </div>
      </section>
    </div>
  );
}
